import React from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { getMessage } from '../../../../../../../lib/translator'
import {
  PICKING_ORDER_STATUSES,
  getPickingOrderStatusClassName,
  getPickingOrderStatusLabel,
} from '../../../../../DeliveryOrdersPicking/constants/status'
import { GROUP_ROLE } from '../../../poaPacklistGroups'
import ReadOnlyPacklistTable from '../../../PacklistView/ReadOnlyPacklistTable'
import PacklistPackingDetails from '../../../PackingDetails'

const ExternalLinkIcon = () => (
  <svg
    className="dsp-order-detail__poa-order-no-icon"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M14 3h7v7h-2V6.4l-9.3 9.3-1.4-1.4L17.6 5H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"
    />
  </svg>
)

// lib/datetime has no formatter for the "21 Sep 2026, 4:12 PM" the design calls for —
// getPrintableTime zero-pads the hour to "04:12 PM".
export const formatAddedAt = (addedAt) => {
  if (!addedAt) {
    return ''
  }
  const parsed = moment(addedAt)
  return parsed.isValid() ? parsed.format('D MMM YYYY, h:mm A') : ''
}

// "Amendment 2" once the amendments are numbered by creation time, else plain "Amendment".
const getRoleLabel = (group, isAmended) => {
  if (!isAmended) {
    return getMessage('picking.orderDetail.poa.group.mainOrder')
  }
  return group.amendmentNumber
    ? getMessage('picking.orderDetail.amendment.numbered', {
        number: group.amendmentNumber,
      })
    : getMessage('picking.orderDetail.poa.group.addedItems')
}

/**
 * One linked delivery order: what it holds and when it was added. Read-only — LIT-541 AC 5/6
 * keep every page action bound to the order that was opened, so this card carries no
 * action controls. The one exception is the order number itself, which links to that
 * order's own detail page (in a new tab, so the current page isn't lost) — plain
 * navigation, not an action-switch, matching the "user opens it directly" workflow the
 * ticket itself points to.
 */
const PoaOrderGroupCard = ({ group, currency, orderStatus, basePath }) => {
  const addedAt = formatAddedAt(group.addedAt)
  const meta = addedAt
    ? getMessage('picking.orderDetail.poa.group.metaWithAddedAt', {
        items: group.itemCount,
        addedAt,
      })
    : getMessage('picking.orderDetail.poa.group.meta', {
        items: group.itemCount,
      })

  // Until LIT-540 pins down whether this is a picking-order or a packlist status, render it
  // only when it is one we have copy for, rather than showing a raw key.
  const showStatus = PICKING_ORDER_STATUSES.includes(group.status)

  const isAmended = group.role === GROUP_ROLE.AMENDED

  return (
    <section className="dsp-order-detail__poa-card" data-testid="dsp-poa-group">
      {/* A plain div, not <header>: this app has a global `header > div { position: fixed }`
          rule (for an unrelated sticky page header) that matches ANY div directly under
          ANY <header>, silently pulling these rows out of the page flow. */}
      <div className="dsp-order-detail__poa-group-header">
        <div className="dsp-order-detail__poa-group-header-row">
          <span
            className={`dsp-order-detail__poa-role-pill${
              isAmended ? '' : ' dsp-order-detail__poa-role-pill--main'
            }`}
          >
            {getRoleLabel(group, isAmended)}
          </span>
          {group.deliveryOrderNo ? (
            <Link
              className="dsp-order-detail__poa-order-no"
              to={`${basePath}/${group.deliveryOrderNo}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              data-testid="dsp-poa-group-order-no"
            >
              {getMessage('picking.orderDetail.poa.group.orderNo', {
                deliveryOrderNo: group.deliveryOrderNo,
              })}
              <ExternalLinkIcon />
            </Link>
          ) : null}
          {showStatus ? (
            <span className="dsp-order-detail__poa-status">
              {getMessage('picking.orderDetail.poa.group.status')}
              {' : '}
              <span
                className={`dsp-order-detail__status-pill ${getPickingOrderStatusClassName(group.status)}`}
              >
                {getPickingOrderStatusLabel(group.status)}
              </span>
            </span>
          ) : null}
        </div>
        <div className="dsp-order-detail__poa-group-header-row">
          <span className="dsp-order-detail__poa-meta">{meta}</span>
        </div>
      </div>

      <ReadOnlyPacklistTable
        packlists={group.packlists}
        currency={currency}
        orderStatus={orderStatus}
      />

      {/* Read-only: PacklistPackingDetails only initialises/reports edits when
          itemEditMode is true, so itemEditMode={false} makes it purely a display of
          whatever crates/packages already exist — no updateCrates handler needed. */}
      <PacklistPackingDetails
        packlistDetails={group.packlists}
        itemEditMode={false}
      />
    </section>
  )
}

export default PoaOrderGroupCard
